import { XCircleIcon } from '@heroicons/react/24/solid';
import CheckIcon from '@heroicons/react/24/solid/CheckIcon';
import PencilIcon from '@heroicons/react/24/solid/PencilIcon';
import TrashIcon from '@heroicons/react/24/solid/TrashIcon';
import { useState } from 'react';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import Modal from '@/components/UI/Modal';

export interface TableHeader {
  name: string;
  dataAttribute: string;
  formatter?: (meta: {
    col: any;
    row: any;
    index: number;
    isEdit: boolean;
  }) => React.ReactNode | React.ReactNode[] | string | null | undefined;
}

export type EditActionFn = (updatedRow: any, index: number) => void;
interface ActionInterface {
  edit?: boolean;
  delete?: boolean;
}
export interface TableProps {
  headers: TableHeader[];
  id: string;
  actions?: ActionInterface;
  onEdit?: EditActionFn;
  onDelete?: EditActionFn;
  data: any;
  handleErrors?: (
    row: any,
    index: number
  ) => {
    hasErrors?: boolean;
    view?: React.ReactNode;
  };
  isLoading?: boolean;
  loadingPlaceholderNode?: React.ReactNode;
  noDataIndicationNode?: React.ReactNode;
}

export const Table = (props: TableProps) => {
  const {
    headers,
    data,
    actions,
    id,
    onEdit,
    onDelete,
    handleErrors = () => {
      return {
        hasErrors: false,
        view: undefined,
      };
    },
  } = props;
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState<{ [id: string]: boolean }>({});
  const [currentRow, setCurrentRow] = useState<number>();
  const toggleEdit = (rowIndex: number) => {
    setIsEdit((prev) => ({ [rowIndex]: !prev?.[rowIndex] }));
    setCurrentRow(rowIndex);
  };

  const toggleShowModal = () => {
    setShowModal((prev) => !prev);
  };

  const displayData = (head: TableHeader, row: any, index: number) => {
    if (head.formatter) {
      return head.formatter({
        col: row[head.dataAttribute],
        row,
        index,
        isEdit: isEdit?.[index],
      });
    }
    return row[head.dataAttribute] || '-';
  };
  const getDataNumber = (rowIndex: number) => {
    // return currentSize * (currentPage - 1) + (rowIndex + 1)
    return rowIndex + 1;
  };

  const handleSubmit = (values: any) => {
    if (onEdit && currentRow !== undefined) {
      onEdit(values, currentRow);
    }
    toggleEdit(currentRow || -1);
    setCurrentRow(undefined);
  };
  return (
    <>
      <HookFormProvider key={currentRow} defaultValues={currentRow !== undefined ? (data || [])[currentRow] : {}}>
        <HookForm onSubmit={handleSubmit} noDefaultClass>
          <table className='divide-y divide-gray-200 w-full'>
            <thead className='bg-gray-50 text-neutralColor-normal'>
              <tr>
                <th className='text-left py-4 pl-2'>S/N</th>
                {headers.map((header, index) => (
                  <th className='capitalize text-left' key={`${id}-${header.name}-${index}`}>
                    {header.name}
                  </th>
                ))}
                {actions && <th className='text-left py-4'>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {data.map((row: any, rowIndex: number) => {
                const { hasErrors, view } = handleErrors(row, rowIndex);
                return (
                  <>
                    <tr key={`${id}-${row.id}-${rowIndex}`} className={hasErrors ? 'bg-tertiaryColor-lightest' : ''}>
                      <td className='py-6 pl-4'>{getDataNumber(rowIndex)}</td>
                      {headers.map((head) => (
                        <td className='py-6' key={`${id}-${head.dataAttribute}-${rowIndex}`}>
                          {displayData(head, row, rowIndex)}
                        </td>
                      ))}
                      {actions && (
                        <td className='py-4'>
                          <div className='flex flex-wrap justify-center items-center gap-2 text-neutralColor-normal'>
                            {actions?.edit &&
                              (isEdit[rowIndex] ? (
                                <div className='flex gap-2'>
                                  <button
                                    type='submit'
                                    id={`${row.id}-save-button`}
                                    className='cursor-pointer text-primaryColor-normal bg-neutralColor-lightest hover:bg-neutralColor-lighter rounded-full p-1'
                                  >
                                    <CheckIcon className='w-5 h-5' />
                                  </button>
                                  <button
                                    type='button'
                                    id={`${row.id}-cancel-button`}
                                    className='cursor-pointer text-tertiaryColor-normal bg-neutralColor-lightest hover:bg-neutralColor-lighter rounded-full p-1'
                                    onClick={() => toggleEdit(rowIndex)}
                                  >
                                    <XCircleIcon className='w-5 h-5' />
                                  </button>
                                </div>
                              ) : (
                                <button
                                  type='button'
                                  onClick={() => toggleEdit(rowIndex)}
                                  id={`${row.id}-edit-button`}
                                  className='cursor-pointer hover:text-primaryColor-normal hover:bg-neutralColor-lightest p-2 rounded-full'
                                >
                                  <PencilIcon className='w-5 h-5' />
                                </button>
                              ))}
                            {actions?.delete && !isEdit[rowIndex] && (
                              <div
                                role='button'
                                onClick={() => {
                                  toggleShowModal();
                                  setCurrentRow(rowIndex);
                                }}
                                id={`${row.id}-delete-button`}
                                className='cursor-pointer hover:text-tertiaryColor-normal hover:bg-neutralColor-lightest p-2 rounded-full'
                              >
                                <TrashIcon className='w-5 h-5' />
                              </div>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                    <tr className={`border-b ${hasErrors && 'border-white'}`}>
                      <td className={hasErrors ? 'bg-tertiaryColor-lightest' : ''} />
                      <td
                        colSpan={headers.length}
                        className={`pb-2 text-xs text-tertiaryColor-normal ${hasErrors ? 'bg-tertiaryColor-lightest' : ''}`}
                      >
                        {view}
                      </td>
                      <td className={hasErrors ? 'bg-tertiaryColor-lightest' : ''}></td>
                    </tr>
                  </>
                );
              })}
            </tbody>
          </table>
        </HookForm>
      </HookFormProvider>
      {showModal && (
        <Modal>
          <h2 className='mt-0 capitalize'>Delete {id}</h2>
          <p>You are about to delete a {id}. Are you sure?</p>
          <div className='flex gap-2 justify-end w-full mt-5'>
            <Button
              label='No'
              btnStyle='btn-neutral'
              btnType='button'
              onClick={() => {
                toggleShowModal();
              }}
            />
            <Button
              label={'Yes'}
              btnStyle='btn-tertiary'
              btnType='button'
              onClick={() => {
                toggleShowModal();
                if (onDelete && currentRow !== undefined) {
                  onDelete(data[currentRow], currentRow);
                }
                setCurrentRow(undefined);
              }}
            />
          </div>
        </Modal>
      )}
    </>
  );
};
